import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import SlidePanel from "../../components/SlidePanel/SlidePanel";
import * as hrmsService from "../../services/hrmsService";
import { FormFields, cleanValues, useMasterOptions } from "./hrForm";
import { JOB_MASTER_KEYS, jobFields } from "./employeeFields";
import { EmptyState, LoadState, PrimaryButton, SecondaryButton } from "./hrComponents";
import { cardStyle, formatDate, statusLabel, tdStyle, thStyle, todayYmd, useLoad } from "./hrUi";

// EMP-02: dated changes to the job. Each event closes the previous row and opens a new one, so
// "who reported to whom, in which department, on that date" can always be answered.
const EVENTS = [
  { value: "TRANSFER", label: "Transfer", keys: ["department_id", "location_id", "site_id", "cost_centre_id"] },
  { value: "PROMOTION", label: "Promotion", keys: ["designation_id", "grade_id"] },
  { value: "MANAGER_CHANGE", label: "Manager change", keys: ["manager_id"] },
  { value: "EMPLOYMENT_TYPE_CHANGE", label: "Employment type change", keys: ["employment_type"] },
  { value: "POLICY_CHANGE", label: "Leave policy change", keys: ["leave_policy_id"] },
  { value: "CONFIRMATION", label: "Confirmation", keys: [] },
  { value: "SUSPENSION", label: "Suspension", keys: [] },
  { value: "REINSTATEMENT", label: "Reinstatement", keys: [] },
];

function EventDrawer({ show, onHide, employeeId, onDone }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const options = useMasterOptions(JOB_MASTER_KEYS);
  useEffect(() => {
    if (show) setValues({ event_type: "TRANSFER", effective_from: todayYmd() });
  }, [show]);
  const event = EVENTS.find((e) => e.value === values.event_type) || EVENTS[0];
  const changeFields = jobFields(options, { excludeEmployee: [employeeId] }).filter((f) => event.keys.includes(f.key));

  const save = async () => {
    setSaving(true);
    try {
      const changes = cleanValues(Object.fromEntries(event.keys.map((k) => [k, values[k]])));
      const res = await hrmsService.addEmployeeEvent(employeeId, { event_type: event.value, effective_from: values.effective_from, notes: values.notes, changes });
      if (res?.success) {
        toast.success(res.message || "Change recorded");
        onDone();
        onHide();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SlidePanel
      show={show}
      onHide={() => !saving && onHide()}
      title="Record a job change"
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving || !values.effective_from}>
            Save
          </PrimaryButton>
        </>
      }
    >
      <FormFields
        values={values}
        onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))}
        fields={[
          { key: "event_type", label: "What changed", type: "select", required: true, options: EVENTS.map(({ value, label }) => ({ value, label })) },
          { key: "effective_from", label: "Effective from", type: "date", required: true, hint: "A date in the future takes effect on that day." },
          ...changeFields,
          { key: "notes", label: "Notes", type: "textarea", max: 500, rows: 2 },
        ]}
      />
    </SlidePanel>
  );
}

// Fixes a mistake on the CURRENT row in place (no new dated event) - unlike EventDrawer, every job
// field is editable together here since a correction isn't about "what kind of change happened".
function CorrectDrawer({ show, onHide, employeeId, row, onDone }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const options = useMasterOptions(JOB_MASTER_KEYS);
  useEffect(() => {
    if (show && row) setValues({ ...row });
  }, [show, row]);

  const save = async () => {
    setSaving(true);
    try {
      const changes = cleanValues(values);
      const res = await hrmsService.correctEmployeeHistory(employeeId, row.id, changes);
      if (res?.success) {
        toast.success(res.message || "Entry corrected");
        onDone();
        onHide();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SlidePanel
      show={show}
      onHide={() => !saving && onHide()}
      title="Correct this entry"
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving}>
            Save
          </PrimaryButton>
        </>
      }
    >
      <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 12 }}>
        This edits the current entry in place - it does not create a new dated change. Use "Record a change" instead if something actually changed on a specific date.
      </div>
      <FormFields values={values} onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))} fields={jobFields(options, { excludeEmployee: [employeeId] })} />
    </SlidePanel>
  );
}

export default function EmployeeJobTab({ employeeId, canManage }) {
  const { data, loading, error, reload } = useLoad(() => hrmsService.getEmployeeHistory(employeeId), [employeeId]);
  const [adding, setAdding] = useState(false);
  const [correcting, setCorrecting] = useState(null);
  const rows = data || [];
  const latestId = rows.length ? rows[rows.length - 1].id : null;
  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      {canManage ? (
        <div className="mb-3">
          <PrimaryButton onClick={() => setAdding(true)}>Record a change</PrimaryButton>
        </div>
      ) : null}
      {rows.length === 0 ? (
        <EmptyState title="No job history" />
      ) : (
        <div style={{ ...cardStyle, overflowX: "auto" }}>
          <table className="table mb-0" style={{ minWidth: 760 }}>
            <thead>
              <tr>
                {["From", "To", "Event", "Department", "Designation", "Location", "Reports to", "Type", ...(canManage ? [""] : [])].map((h) => (
                  <th key={h} style={thStyle}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...rows].reverse().map((r) => (
                <tr key={r.id}>
                  <td style={tdStyle}>{formatDate(r.effective_from)}</td>
                  <td style={tdStyle}>{r.effective_to ? formatDate(r.effective_to) : "Current"}</td>
                  <td style={tdStyle}>
                    {statusLabel(r.event_type)}
                    {r.notes ? <div style={{ fontSize: 12, color: "#6B7280" }}>{r.notes}</div> : null}
                  </td>
                  <td style={tdStyle}>{r.department_name || "—"}</td>
                  <td style={tdStyle}>{r.designation_name || "—"}</td>
                  <td style={tdStyle}>{r.location_name || "—"}</td>
                  <td style={tdStyle}>{r.manager_name || "—"}</td>
                  <td style={tdStyle}>{statusLabel(r.employment_type) || "—"}</td>
                  {canManage ? (
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      {r.id === latestId ? (
                        <button type="button" style={{ fontSize: 13, fontWeight: 600, background: "none", border: "none", color: "#2563EB", cursor: "pointer" }} onClick={() => setCorrecting(r)}>
                          Correct this entry
                        </button>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <EventDrawer show={adding} onHide={() => setAdding(false)} employeeId={employeeId} onDone={reload} />
      <CorrectDrawer show={!!correcting} onHide={() => setCorrecting(null)} employeeId={employeeId} row={correcting} onDone={reload} />
    </LoadState>
  );
}
